import { html, nothing } from "lit";
import type {
  AgentIdentityResult,
  AgentsFilesListResult,
  AgentsListResult,
  ChannelsStatusSnapshot,
  CronJob,
  CronStatus,
  SkillStatusReport,
  ToolsCatalogResult,
} from "../types.ts";
import {
  renderAgentFiles,
  renderAgentChannels,
  renderAgentCron,
} from "./agents-panels-status-files.ts";
import { renderAgentTools, renderAgentSkills } from "./agents-panels-tools-skills.ts";
import {
  agentBadgeText,
  buildAgentContext,
  buildModelOptions,
  normalizeAgentLabel,
  normalizeModelValue,
  parseFallbackList,
  resolveAgentConfig,
  resolveAgentEmoji,
  resolveEffectiveModelFallbacks,
  resolveModelLabel,
  resolveModelPrimary,
} from "./agents-utils.ts";

export type AgentsPanel = "overview" | "files" | "tools" | "skills" | "channels" | "cron" | "providers";

export type AgentsProps = {
  loading: boolean;
  error: string | null;
  agentsList: AgentsListResult | null;
  selectedAgentId: string | null;
  activePanel: AgentsPanel;
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  channelsLoading: boolean;
  channelsError: string | null;
  channelsSnapshot: ChannelsStatusSnapshot | null;
  channelsLastSuccess: number | null;
  cronLoading: boolean;
  cronStatus: CronStatus | null;
  cronJobs: CronJob[];
  cronError: string | null;
  agentFilesLoading: boolean;
  agentFilesError: string | null;
  agentFilesList: AgentsFilesListResult | null;
  agentFileActive: string | null;
  agentFileContents: Record<string, string>;
  agentFileDrafts: Record<string, string>;
  agentFileSaving: boolean;
  agentIdentityLoading: boolean;
  agentIdentityError: string | null;
  agentIdentityById: Record<string, AgentIdentityResult>;
  agentSkillsLoading: boolean;
  agentSkillsReport: SkillStatusReport | null;
  agentSkillsError: string | null;
  agentSkillsAgentId: string | null;
  toolsCatalogLoading: boolean;
  toolsCatalogError: string | null;
  toolsCatalogResult: ToolsCatalogResult | null;
  skillsFilter: string;
  onRefresh: () => void;
  onSelectAgent: (agentId: string) => void;
  onSelectPanel: (panel: AgentsPanel) => void;
  onLoadFiles: (agentId: string) => void;
  onSelectFile: (name: string) => void;
  onFileDraftChange: (name: string, content: string) => void;
  onFileReset: (name: string) => void;
  onFileSave: (name: string) => void;
  onToolsProfileChange: (agentId: string, profile: string | null, clearAllow: boolean) => void;
  onToolsOverridesChange: (agentId: string, alsoAllow: string[], deny: string[]) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onModelChange: (agentId: string, modelId: string | null) => void;
  onModelFallbacksChange: (agentId: string, fallbacks: string[]) => void;
  onChannelsRefresh: () => void;
  onCronRefresh: () => void;
  onSkillsFilterChange: (next: string) => void;
  onSkillsRefresh: () => void;
  onAgentSkillToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  onAgentSkillsClear: (agentId: string) => void;
  onAgentSkillsDisableAll: (agentId: string) => void;
  onProviderConfigChange: (provider: string, apiKey: string) => void;
  onProviderBaseUrlChange: (provider: string, baseUrl: string) => void;
  onDefaultModelChange: (modelId: string | null) => void;
  onImportCredentials: (url: string, token: string) => Promise<{ ok: boolean; error?: string }>;
  credentialsImported: boolean;
  credentialsImporting: boolean;
  credentialsError: string | null;
};

export type AgentContext = {
  workspace: string;
  model: string;
  identityName: string;
  identityEmoji: string;
  skillsLabel: string;
  isDefault: boolean;
};

export function renderAgents(props: AgentsProps) {
  const agents = props.agentsList?.agents ?? [];
  const defaultId = props.agentsList?.defaultId ?? null;
  const selectedId = props.selectedAgentId ?? defaultId ?? agents[0]?.id ?? null;
  const selectedAgent = selectedId
    ? (agents.find((agent) => agent.id === selectedId) ?? null)
    : null;

  return html`
    <div class="agents-layout">
      <section class="card agents-sidebar">
        <div class="row" style="justify-content: space-between;">
          <div>
            <div class="card-title">Agents</div>
            <div class="card-sub">${agents.length} configured.</div>
          </div>
          <button class="btn btn--sm" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        ${
          props.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
            : nothing
        }
        <div class="agent-list" style="margin-top: 12px;">
          ${
            agents.length === 0
              ? html`
                  <div class="muted">No agents found.</div>
                `
              : agents.map((agent) => {
                  const badge = agentBadgeText(agent.id, defaultId);
                  const emoji = resolveAgentEmoji(agent, props.agentIdentityById[agent.id] ?? null);
                  return html`
                    <button
                      type="button"
                      class="agent-row ${selectedId === agent.id ? "active" : ""}"
                      @click=${() => props.onSelectAgent(agent.id)}
                    >
                      <div class="agent-avatar">${emoji || normalizeAgentLabel(agent).slice(0, 1)}</div>
                      <div class="agent-info">
                        <div class="agent-title">${normalizeAgentLabel(agent)}</div>
                        <div class="agent-sub mono">${agent.id}</div>
                      </div>
                      ${badge ? html`<span class="agent-pill">${badge}</span>` : nothing}
                    </button>
                  `;
                })
          }
        </div>
      </section>
      <section class="agents-main">
        ${
          !selectedAgent
            ? html`
                <div class="card">
                  <div class="card-title">Select an agent</div>
                  <div class="card-sub">Pick an agent to inspect its workspace and tools.</div>
                </div>
              `
            : html`
                ${renderAgentHeader(
                  selectedAgent,
                  defaultId,
                  props.agentIdentityById[selectedAgent.id] ?? null,
                )}
                ${renderAgentTabs(props.activePanel, (panel) => props.onSelectPanel(panel))}
                ${
                  props.activePanel === "overview"
                    ? renderAgentOverview({
                        agent: selectedAgent,
                        defaultId,
                        configForm: props.configForm,
                        agentFilesList: props.agentFilesList,
                        agentIdentity: props.agentIdentityById[selectedAgent.id] ?? null,
                        agentIdentityError: props.agentIdentityError,
                        agentIdentityLoading: props.agentIdentityLoading,
                        configLoading: props.configLoading,
                        configSaving: props.configSaving,
                        configDirty: props.configDirty,
                        onConfigReload: props.onConfigReload,
                        onConfigSave: props.onConfigSave,
                        onModelChange: props.onModelChange,
                        onModelFallbacksChange: props.onModelFallbacksChange,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "files"
                    ? renderAgentFiles({
                        agentId: selectedAgent.id,
                        agentFilesList: props.agentFilesList,
                        agentFilesLoading: props.agentFilesLoading,
                        agentFilesError: props.agentFilesError,
                        agentFileActive: props.agentFileActive,
                        agentFileContents: props.agentFileContents,
                        agentFileDrafts: props.agentFileDrafts,
                        agentFileSaving: props.agentFileSaving,
                        onLoadFiles: props.onLoadFiles,
                        onSelectFile: props.onSelectFile,
                        onFileDraftChange: props.onFileDraftChange,
                        onFileReset: props.onFileReset,
                        onFileSave: props.onFileSave,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "tools"
                    ? renderAgentTools({
                        agentId: selectedAgent.id,
                        configForm: props.configForm,
                        configLoading: props.configLoading,
                        configSaving: props.configSaving,
                        configDirty: props.configDirty,
                        toolsCatalogLoading: props.toolsCatalogLoading,
                        toolsCatalogError: props.toolsCatalogError,
                        toolsCatalogResult: props.toolsCatalogResult,
                        onProfileChange: props.onToolsProfileChange,
                        onOverridesChange: props.onToolsOverridesChange,
                        onConfigReload: props.onConfigReload,
                        onConfigSave: props.onConfigSave,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "skills"
                    ? renderAgentSkills({
                        agentId: selectedAgent.id,
                        report: props.agentSkillsReport,
                        loading: props.agentSkillsLoading,
                        error: props.agentSkillsError,
                        activeAgentId: props.agentSkillsAgentId,
                        configForm: props.configForm,
                        configLoading: props.configLoading,
                        configSaving: props.configSaving,
                        configDirty: props.configDirty,
                        filter: props.skillsFilter,
                        onFilterChange: props.onSkillsFilterChange,
                        onRefresh: props.onSkillsRefresh,
                        onToggle: props.onAgentSkillToggle,
                        onClear: props.onAgentSkillsClear,
                        onDisableAll: props.onAgentSkillsDisableAll,
                        onConfigReload: props.onConfigReload,
                        onConfigSave: props.onConfigSave,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "channels"
                    ? renderAgentChannels({
                        context: buildAgentContext(
                          selectedAgent,
                          props.configForm,
                          props.agentFilesList,
                          defaultId,
                          props.agentIdentityById[selectedAgent.id] ?? null,
                        ),
                        configForm: props.configForm,
                        snapshot: props.channelsSnapshot,
                        loading: props.channelsLoading,
                        error: props.channelsError,
                        lastSuccess: props.channelsLastSuccess,
                        onRefresh: props.onChannelsRefresh,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "cron"
                    ? renderAgentCron({
                        context: buildAgentContext(
                          selectedAgent,
                          props.configForm,
                          props.agentFilesList,
                          defaultId,
                          props.agentIdentityById[selectedAgent.id] ?? null,
                        ),
                        agentId: selectedAgent.id,
                        jobs: props.cronJobs,
                        status: props.cronStatus,
                        loading: props.cronLoading,
                        error: props.cronError,
                        onRefresh: props.onCronRefresh,
                      })
                    : nothing
                }
                ${
                  props.activePanel === "providers"
                    ? renderProvidersPanel({
                        configForm: props.configForm,
                        configLoading: props.configLoading,
                        configSaving: props.configSaving,
                        configDirty: props.configDirty,
                        onConfigReload: props.onConfigReload,
                        onConfigSave: props.onConfigSave,
                        onProviderConfigChange: props.onProviderConfigChange,
                        onProviderBaseUrlChange: props.onProviderBaseUrlChange,
                        onDefaultModelChange: props.onDefaultModelChange,
                      })
                    : nothing
                }
              `
        }
      </section>
    </div>
  `;
}

function renderAgentHeader(
  agent: AgentsListResult["agents"][number],
  defaultId: string | null,
  agentIdentity: AgentIdentityResult | null,
) {
  const badge = agentBadgeText(agent.id, defaultId);
  const displayName = normalizeAgentLabel(agent);
  const subtitle = agent.identity?.theme?.trim() || "Agent workspace and routing.";
  const emoji = resolveAgentEmoji(agent, agentIdentity);
  return html`
    <section class="card agent-header">
      <div class="agent-header-main">
        <div class="agent-avatar agent-avatar--lg">${emoji || displayName.slice(0, 1)}</div>
        <div>
          <div class="card-title">${displayName}</div>
          <div class="card-sub">${subtitle}</div>
        </div>
      </div>
      <div class="agent-header-meta">
        <div class="mono">${agent.id}</div>
        ${badge ? html`<span class="agent-pill">${badge}</span>` : nothing}
      </div>
    </section>
  `;
}

function renderAgentTabs(active: AgentsPanel, onSelect: (panel: AgentsPanel) => void) {
  const tabs: Array<{ id: AgentsPanel; label: string }> = [
    { id: "overview", label: "Overview" },
    { id: "files", label: "Files" },
    { id: "tools", label: "Tools" },
    { id: "skills", label: "Skills" },
    { id: "channels", label: "Channels" },
    { id: "cron", label: "Cron Jobs" },
    { id: "providers", label: "AI Providers" },
  ];
  return html`
    <div class="agent-tabs">
      ${tabs.map(
        (tab) => html`
          <button
            class="agent-tab ${active === tab.id ? "active" : ""}"
            type="button"
            @click=${() => onSelect(tab.id)}
          >
            ${tab.label}
          </button>
        `,
      )}
    </div>
  `;
}

function renderAgentOverview(params: {
  agent: AgentsListResult["agents"][number];
  defaultId: string | null;
  configForm: Record<string, unknown> | null;
  agentFilesList: AgentsFilesListResult | null;
  agentIdentity: AgentIdentityResult | null;
  agentIdentityLoading: boolean;
  agentIdentityError: string | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onModelChange: (agentId: string, modelId: string | null) => void;
  onModelFallbacksChange: (agentId: string, fallbacks: string[]) => void;
}) {
  const {
    agent,
    configForm,
    agentFilesList,
    agentIdentity,
    agentIdentityLoading,
    agentIdentityError,
    configLoading,
    configSaving,
    configDirty,
    onConfigReload,
    onConfigSave,
    onModelChange,
    onModelFallbacksChange,
  } = params;
  const config = resolveAgentConfig(configForm, agent.id);
  const workspaceFromFiles =
    agentFilesList && agentFilesList.agentId === agent.id ? agentFilesList.workspace : null;
  const workspace =
    workspaceFromFiles || config.entry?.workspace || config.defaults?.workspace || "default";
  const model = config.entry?.model
    ? resolveModelLabel(config.entry?.model)
    : resolveModelLabel(config.defaults?.model);
  const defaultModel = resolveModelLabel(config.defaults?.model);
  const modelPrimary =
    resolveModelPrimary(config.entry?.model) || (model !== "-" ? normalizeModelValue(model) : null);
  const defaultPrimary =
    resolveModelPrimary(config.defaults?.model) ||
    (defaultModel !== "-" ? normalizeModelValue(defaultModel) : null);
  const effectivePrimary = modelPrimary ?? defaultPrimary ?? null;
  const modelFallbacks = resolveEffectiveModelFallbacks(
    config.entry?.model,
    config.defaults?.model,
  );
  const fallbackText = modelFallbacks ? modelFallbacks.join(", ") : "";
  const identityName =
    agentIdentity?.name?.trim() ||
    agent.identity?.name?.trim() ||
    agent.name?.trim() ||
    config.entry?.name ||
    "-";
  const resolvedEmoji = resolveAgentEmoji(agent, agentIdentity);
  const identityEmoji = resolvedEmoji || "-";
  const skillFilter = Array.isArray(config.entry?.skills) ? config.entry?.skills : null;
  const skillCount = skillFilter?.length ?? null;
  const identityStatus = agentIdentityLoading
    ? "Loading…"
    : agentIdentityError
      ? "Unavailable"
      : "";
  const isDefault = Boolean(params.defaultId && agent.id === params.defaultId);

  return html`
    <section class="card">
      <div class="card-title">Overview</div>
      <div class="card-sub">Workspace paths and identity metadata.</div>
      <div class="agents-overview-grid" style="margin-top: 16px;">
        <div class="agent-kv">
          <div class="label">Workspace</div>
          <div class="mono">${workspace}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Primary Model</div>
          <div class="mono">${model}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Identity Name</div>
          <div>${identityName}</div>
          ${identityStatus ? html`<div class="agent-kv-sub muted">${identityStatus}</div>` : nothing}
        </div>
        <div class="agent-kv">
          <div class="label">Default</div>
          <div>${isDefault ? "yes" : "no"}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Identity Emoji</div>
          <div>${identityEmoji}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Skills Filter</div>
          <div>${skillFilter ? `${skillCount} selected` : "all skills"}</div>
        </div>
      </div>

      <div class="agent-model-select" style="margin-top: 20px;">
        <div class="label">Model Selection</div>
        <div class="row" style="gap: 12px; flex-wrap: wrap;">
          <label class="field" style="min-width: 260px; flex: 1;">
            <span>Primary model${isDefault ? " (default)" : ""}</span>
            <select
              .value=${effectivePrimary ?? ""}
              ?disabled=${!configForm || configLoading || configSaving}
              @change=${(e: Event) =>
                onModelChange(agent.id, (e.target as HTMLSelectElement).value || null)}
            >
              ${
                isDefault
                  ? nothing
                  : html`
                      <option value="">
                        ${defaultPrimary ? `Inherit default (${defaultPrimary})` : "Inherit default"}
                      </option>
                    `
              }
              ${buildModelOptions(configForm, effectivePrimary ?? undefined)}
            </select>
          </label>
          <label class="field" style="min-width: 260px; flex: 1;">
            <span>Fallbacks (comma-separated)</span>
            <input
              .value=${fallbackText}
              ?disabled=${!configForm || configLoading || configSaving}
              placeholder="provider/model, provider/model"
              @input=${(e: Event) =>
                onModelFallbacksChange(
                  agent.id,
                  parseFallbackList((e.target as HTMLInputElement).value),
                )}
            />
          </label>
        </div>
        <div class="row" style="justify-content: flex-end; gap: 8px;">
          <button class="btn btn--sm" ?disabled=${configLoading} @click=${onConfigReload}>
            Reload Config
          </button>
          <button
            class="btn btn--sm primary"
            ?disabled=${configSaving || !configDirty}
            @click=${onConfigSave}
          >
            ${configSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </section>
  `;
}

function renderProvidersPanel(params: {
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onProviderConfigChange: (provider: string, apiKey: string) => void;
  onProviderBaseUrlChange: (provider: string, baseUrl: string) => void;
  onDefaultModelChange: (modelId: string | null) => void;
}) {
  const { configForm, configLoading, configSaving, configDirty } = params;
  const agents = (configForm?.agents as Record<string, unknown> | undefined);
  const defaults = (agents?.defaults as Record<string, unknown> | undefined);
  const currentModel = (defaults?.model as string | undefined) ?? "";

  const providers = (configForm?.models as Record<string, unknown> | undefined)?.providers as
    | Record<string, unknown>
    | undefined;

  const getApiKey = (providerKey: string): string => {
    const provider = providers?.[providerKey] as Record<string, unknown> | undefined;
    const key = provider?.apiKey as string | undefined;
    return key || "";
  };

  const getBaseUrl = (providerKey: string): string => {
    const provider = providers?.[providerKey] as Record<string, unknown> | undefined;
    const url = provider?.baseUrl as string | undefined;
    return url || "";
  };

  const availableModels = [
    { group: "Anthropic", models: [
      { value: "anthropic/claude-opus-4-6", label: "Claude Opus 4.6" },
      { value: "anthropic/claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
      { value: "anthropic/claude-haiku-4-5", label: "Claude Haiku 4.5" },
    ]},
    { group: "OpenAI", models: [
      { value: "openai/gpt-5.1", label: "GPT-5.1" },
      { value: "openai/gpt-5.1-mini", label: "GPT-5.1 Mini" },
      { value: "openai/o1", label: "O1" },
      { value: "openai/o1-mini", label: "O1 Mini" },
    ]},
    { group: "Google", models: [
      { value: "google/gemini-2.5-flash", label: "Gemini 2.5 Flash" },
      { value: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro" },
      { value: "google/gemini-2.0-flash", label: "Gemini 2.0 Flash" },
      { value: "google/gemini-pro-1.5", label: "Gemini 1.5 Pro" },
    ]},
    { group: "Z.AI (Zhipu)", models: [
      { value: "zai/glm-5", label: "GLM-5" },
      { value: "zai/glm-4.7", label: "GLM-4.7" },
      { value: "zai/glm-4.7-flash", label: "GLM-4.7 Flash" },
      { value: "zai/glm-4.6", label: "GLM-4.6" },
    ]},
    { group: "OpenRouter", models: [
      { value: "openrouter/anthropic/claude-opus-4-6", label: "Claude Opus 4.6 (via OpenRouter)" },
      { value: "openrouter/openai/gpt-5.1", label: "GPT-5.1 (via OpenRouter)" },
    ]},
    { group: "Ollama", models: [
      { value: "ollama/llama3.3:70b", label: "Llama 3.3 70B (local)" },
      { value: "ollama/qwen2.5-coder:32b", label: "Qwen2.5 Coder 32B (local)" },
      { value: "ollama/deepseek-r1:70b", label: "DeepSeek R1 70B (local)" },
    ]},
  ];

  const renderProvider = (provParams: {
    name: string;
    providerKey: string;
    envVar: string;
    apiKeyExample: string;
    signupUrl: string;
    notes?: string;
    showBaseUrl?: boolean;
    baseUrlExample?: string;
  }) => {
    const currentValue = getApiKey(provParams.providerKey);
    const currentBaseUrl = getBaseUrl(provParams.providerKey);
    const hasKey = currentValue.length > 0;
    const hasBaseUrl = currentBaseUrl.length > 0;

    return html`
      <div style="margin-top: 16px; padding: 16px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px;">
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
          <h3 style="font-size: 14px; font-weight: 600; margin: 0;">${provParams.name}</h3>
          <a href="${provParams.signupUrl}" target="_blank" rel="noopener"
             style="font-size: 12px; color: var(--text-link);">
            Get API Key →
          </a>
        </div>
        ${
          provParams.notes
            ? html`<p style="color: var(--text-muted); font-size: 12px; margin: 4px 0 12px;">
                ${provParams.notes}
              </p>`
            : nothing
        }
        <div style="margin-top: 12px;">
          <label style="display: block; margin-bottom: 4px; font-size: 13px; font-weight: 500;">
            API Key ${hasKey ? html`<span style="color: var(--text-success); font-weight: normal;">✓ Configured</span>` : ""}
          </label>
          <input
            type="password"
            class="input"
            placeholder="${provParams.apiKeyExample}"
            value="${currentValue}"
            ?disabled=${configLoading || configSaving}
            @input=${(e: Event) => {
              const target = e.target as HTMLInputElement;
              params.onProviderConfigChange(provParams.providerKey, target.value);
            }}
            style="width: 100%; font-family: monospace; font-size: 13px;"
          />
          <div style="margin-top: 4px; font-size: 12px; color: var(--text-muted);">
            ${hasKey ? `Current: ${"•".repeat(40)}` : "Enter your API key to enable this provider"}
          </div>
        </div>
        ${
          provParams.showBaseUrl
            ? html`
                <div style="margin-top: 16px;">
                  <label style="display: block; margin-bottom: 4px; font-size: 13px; font-weight: 500;">
                    Base URL ${hasBaseUrl ? html`<span style="color: var(--text-success); font-weight: normal;">✓ Custom</span>` : html`<span style="color: var(--text-muted); font-weight: normal;">(Optional)</span>`}
                  </label>
                  <input
                    type="text"
                    class="input"
                    placeholder="${provParams.baseUrlExample || "https://api.example.com/v1"}"
                    value="${currentBaseUrl}"
                    ?disabled=${configLoading || configSaving}
                    @input=${(e: Event) => {
                      const target = e.target as HTMLInputElement;
                      params.onProviderBaseUrlChange(provParams.providerKey, target.value);
                    }}
                    style="width: 100%; font-family: monospace; font-size: 13px;"
                  />
                  <div style="margin-top: 4px; font-size: 12px; color: var(--text-muted);">
                    ${hasBaseUrl ? `Using: ${currentBaseUrl}` : "Leave empty to use default endpoint"}
                  </div>
                </div>
              `
            : nothing
        }
      </div>
    `;
  };

  return html`
    <section class="card">
      <div class="card-title">AI Provider Configuration</div>
      <div class="card-sub">Select your default AI model and configure provider API keys.</div>

      <div style="margin-top: 16px;">
        <div class="callout info">
          <strong>Quick Setup (3 steps)</strong>
          <p style="margin-top: 8px; margin-bottom: 4px;">
            1. Select your AI model below<br>
            2. Enter your API key for that provider<br>
            3. Click "Save Configuration" → Done!
          </p>
          <p style="margin-top: 8px; font-size: 12px; color: var(--text-muted);">
            Your configuration is saved and ready to use immediately - no restart needed.
          </p>
        </div>

        <div style="margin-top: 24px; padding-bottom: 24px; border-bottom: 1px solid var(--border);">
          <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 12px;">Default AI Model</h3>
          <p style="color: var(--text-muted); font-size: 13px; margin-bottom: 12px;">
            Select the AI model to use for all agents by default. Individual agents can override this setting.
          </p>
          <label class="field" style="max-width: 500px;">
            <span>Model</span>
            <select
              class="input"
              .value=${currentModel}
              ?disabled=${!configForm || configLoading || configSaving}
              @change=${(e: Event) => {
                const value = (e.target as HTMLSelectElement).value;
                params.onDefaultModelChange(value || null);
              }}
              style="width: 100%;"
            >
              <option value="">Select a model...</option>
              ${availableModels.map(group => html`
                <optgroup label="${group.group}">
                  ${group.models.map(model => html`
                    <option value="${model.value}" ?selected=${currentModel === model.value}>
                      ${model.label}
                    </option>
                  `)}
                </optgroup>
              `)}
            </select>
          </label>
          ${currentModel ? html`
            <div style="margin-top: 8px; font-size: 12px; color: var(--text-success);">
              ✓ Current model: ${currentModel}
            </div>
          ` : html`
            <div style="margin-top: 8px; font-size: 12px; color: var(--text-warning);">
              ⚠ No model selected. Please select a model to use OpenClaw.
            </div>
          `}
        </div>

        <div style="margin-top: 24px; padding: 16px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px;">
          <h3 style="font-size: 14px; font-weight: 600; margin: 0 0 8px;">Quick Import (OneHammer Credentials)</h3>
          <p style="color: var(--text-muted); font-size: 12px; margin-bottom: 12px;">
            Paste your credentials API URL and bearer token to auto-configure the AI provider.
          </p>
          ${params.credentialsImported
            ? html`
              <div style="padding: 12px; background: var(--bg-input); border-radius: 6px; color: var(--text-success); font-weight: 500;">
                ✓ AI Provider configured successfully via Quick Import.
              </div>
            `
            : html`
              <div style="display: flex; flex-direction: column; gap: 8px;">
                <input
                  type="text"
                  class="input"
                  id="import-credentials-url"
                  placeholder="https://your-domain/api/clients/me/claude-credentials?instance=username"
                  style="width: 100%; font-family: monospace; font-size: 12px;"
                />
                <input
                  type="password"
                  class="input"
                  id="import-credentials-token"
                  placeholder="Bearer token (JWT)"
                  style="width: 100%; font-family: monospace; font-size: 13px;"
                />
                <div style="display: flex; align-items: center; gap: 8px;">
                  <button
                    class="btn primary btn--sm"
                    ?disabled=${params.credentialsImporting}
                    @click=${() => {
                      const urlInput = document.getElementById("import-credentials-url") as HTMLInputElement;
                      const tokenInput = document.getElementById("import-credentials-token") as HTMLInputElement;
                      if (urlInput && tokenInput) {
                        params.onImportCredentials(urlInput.value, tokenInput.value);
                      }
                    }}
                  >
                    ${params.credentialsImporting ? "Importing..." : "Import Credentials"}
                  </button>
                  ${params.credentialsError
                    ? html`<span style="color: var(--danger-color, #d14343); font-size: 12px;">${params.credentialsError}</span>`
                    : nothing}
                </div>
              </div>
            `
          }
        </div>

        <h2 style="font-size: 16px; font-weight: 600; margin: 24px 0 12px;">Provider API Keys</h2>

        ${renderProvider({
          name: "Anthropic (Claude)",
          providerKey: "anthropic",
          envVar: "ANTHROPIC_API_KEY",
          apiKeyExample: "sk-ant-api03-...",
          signupUrl: "https://console.anthropic.com/",
          notes: "Recommended - Most capable AI for coding and complex tasks",
          showBaseUrl: true,
          baseUrlExample: "https://api.anthropic.com",
        })}

        ${renderProvider({
          name: "OpenAI (GPT)",
          providerKey: "openai",
          envVar: "OPENAI_API_KEY",
          apiKeyExample: "sk-...",
          signupUrl: "https://platform.openai.com/api-keys",
          showBaseUrl: true,
          baseUrlExample: "https://api.openai.com/v1",
        })}

        ${renderProvider({
          name: "Google Gemini",
          providerKey: "google",
          envVar: "GEMINI_API_KEY",
          apiKeyExample: "AIza...",
          signupUrl: "https://aistudio.google.com/apikey",
          notes: "Free tier available with generous limits",
          showBaseUrl: true,
          baseUrlExample: "https://generativelanguage.googleapis.com/v1beta",
        })}

        ${renderProvider({
          name: "Z.AI (Zhipu GLM)",
          providerKey: "zai",
          envVar: "ZAI_API_KEY",
          apiKeyExample: "920f3ef...",
          signupUrl: "https://z.ai/model-api",
          notes: "Chinese AI provider with GLM-5 and GLM-4 models. OpenAI-compatible API with coding support.",
          showBaseUrl: true,
          baseUrlExample: "https://api.z.ai/api/coding/paas/v4",
        })}

        ${renderProvider({
          name: "OpenRouter",
          providerKey: "openrouter",
          envVar: "OPENROUTER_API_KEY",
          apiKeyExample: "sk-or-v1-...",
          signupUrl: "https://openrouter.ai/keys",
          notes: "Access multiple AI models through a single API",
        })}

        ${renderProvider({
          name: "MiniMax",
          providerKey: "minimax",
          envVar: "MINIMAX_API_KEY",
          apiKeyExample: "...",
          signupUrl: "https://platform.minimaxi.com/",
          notes: "Chinese AI provider with competitive pricing",
        })}

        ${renderProvider({
          name: "Ollama (Local Models)",
          providerKey: "ollama",
          envVar: "OLLAMA_BASE_URL",
          apiKeyExample: "http://host.docker.internal:11434",
          signupUrl: "https://ollama.com/",
          notes: "Run AI models locally. Enter base URL instead of API key.",
        })}

        <div style="margin-top: 24px; padding-top: 24px; border-top: 1px solid var(--border);">
          <div class="row" style="justify-content: flex-end; gap: 8px;">
            <button class="btn btn--sm" ?disabled=${configLoading} @click=${params.onConfigReload}>
              Reload Config
            </button>
            <button
              class="btn btn--sm primary"
              ?disabled=${configSaving || !configDirty}
              @click=${params.onConfigSave}
            >
              ${configSaving ? "Saving…" : "Save Configuration"}
            </button>
          </div>
        </div>

        <div style="margin-top: 16px;">
          <div class="callout" style="background: var(--bg-input); border: 1px solid var(--border);">
            <p style="font-size: 13px; margin-bottom: 8px;">
              <strong>After saving:</strong>
            </p>
            <p style="font-size: 13px; margin: 0; line-height: 1.6;">
              Your AI provider is now configured! Go to <strong>Channels</strong> page → Click <strong>"Start"</strong> on OneHammer Zalo → Send a test message to get AI responses.
            </p>
          </div>
        </div>
      </div>
    </section>
  `;
}
